import React, {useCallback} from "react";
import Form from "react-bootstrap/Form";
import {
  MODE_CREATE,
  MODE_EDIT,
  PERIODICITY_DAILY,
  PERIODICITY_FIRST_MONDAY_MONTH,
  PERIODICITY_FIRST_WORKING_DAY_MONTH,
  PERIODICITY_MONTHLY,
  PERIODICITY_WEEKLY,
  PERIODICITY_YEARLY
} from "../../../codes";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Editor from "../../../common/Editor";
import {RadioField} from "../../../common/RadioField";
import useTransactionEditor, {TYPE_CREDIT, TYPE_DEBIT, TYPE_TRANSFER} from "./useTransactionEditor";
import InputGroup from "react-bootstrap/InputGroup";
import {FormControlTags} from "../../../common/FormControlTags";
import {DatePicker} from "../../../common/DatePicker";
import {AccountsDropdown} from "../../../common/AccountsDropdown";
import {CategoriesDropdown} from "../../../common/CategoriesDropdown";
import {AppStaticDropdown} from "../../../common/AppStaticDropdown";
import useProject from "../../../util/useProject";
import {DeleteModal} from "../../../common/AppModal";

export const TransactionEditor = () => {
  const {
    show,
    t,
    mode,
    submit,
    hideTransactionEditor,
    editedTransaction,
    updateEditedTransaction,
    availableAccounts,
    availableCategories,
    deleteConfirmationModal,
    showDeleteConfirmationModal,
    hideDeleteConfirmationModal,
    onDeleteTransaction
  } = useTransactionEditor();

  const {runWithExpiration} = useProject();

  const typeChoices = [
    {value: TYPE_DEBIT, label: t("transaction_editor_type_debit"), activeClass: "danger"},
    {value: TYPE_CREDIT, label: t("transaction_editor_type_credit"), activeClass: "success"}
  ];

  if (availableAccounts.length > 1) {
    typeChoices.push({value: TYPE_TRANSFER, label: t("transaction_editor_type_transfer"), activeClass: "default"});
  }

  const periodicities = [
    {value: null, label: t("periodicity_none")},
    {value: PERIODICITY_DAILY, label: t("periodicity_daily")},
    {value: PERIODICITY_WEEKLY, label: t("periodicity_weekly")},
    {value: PERIODICITY_MONTHLY, label: t("periodicity_monthly")},
    {value: PERIODICITY_YEARLY, label: t("periodicity_yearly")},
    {value: PERIODICITY_FIRST_MONDAY_MONTH, label: t("periodicity_first_monday_month")},
    {value: PERIODICITY_FIRST_WORKING_DAY_MONTH, label: t("periodicity_first_working_day_month")},
  ];

  return <Editor
    show={show}
    title={t(mode === MODE_CREATE ? "transaction_editor_title_add" : "transaction_editor_title_edit")}
    onShow={useCallback(() => document.getElementById("transaction-editor-account").focus(), [])}
    onSubmit={runWithExpiration(submit)}
    onCancel={hideTransactionEditor}
    onDelete={mode === MODE_EDIT ? runWithExpiration(showDeleteConfirmationModal) : null}
    submitLabel={t("transaction_editor_button_save")}
    cancelLabel={t("transaction_editor_button_cancel")}
  >
    <Form.Group as={Row} className="mb-2">
      <Form.Label column sm={3}>{t("transaction_editor_field_type")}</Form.Label>
      <Col sm={9}>
        <RadioField
          value={editedTransaction.type}
          choices={typeChoices}
          onChange={updateEditedTransaction("type")}
        />
      </Col>
    </Form.Group>

    <Form.Group as={Row} className="mb-2" controlId="transaction-editor-account">
      <Form.Label column sm={3}>{t("transaction_editor_field_account")}</Form.Label>
      <Col sm={9}>
        <AccountsDropdown
          value={editedTransaction.account}
          onChange={updateEditedTransaction("account")}
          accounts={availableAccounts}
        />
      </Col>
    </Form.Group>

    {editedTransaction.type !== TYPE_TRANSFER &&
      <Form.Group as={Row} className="mb-2" controlId="transaction-editor-payee">
        <Form.Label column sm={3}>{t("transaction_editor_field_payee")}</Form.Label>
        <Col sm={9}>
          <Form.Control
            type="text"
            value={editedTransaction.payee}
            onChange={updateEditedTransaction("payee")}
            maxLength="100"
            isInvalid={editedTransaction.errors.payee}
          />
          <Form.Control.Feedback type="invalid">{t(editedTransaction.errors.payee)}</Form.Control.Feedback>

          {editedTransaction.payeeDisplay &&
            <Form.Text className="text-muted">Sera affiché comme : {editedTransaction.payeeDisplay}</Form.Text>}
        </Col>
      </Form.Group>}

    {editedTransaction.type === TYPE_TRANSFER &&
      <Form.Group as={Row} className="mb-2" controlId="transaction-editor-payee">
        <Form.Label column sm={3}>{t("transaction_editor_field_to_account")}</Form.Label>
        <Col sm={9}>
          <AccountsDropdown
            value={editedTransaction.payee}
            onChange={updateEditedTransaction("other_account")}
            accounts={availableAccounts.filter(a => a.id !== editedTransaction.account)}
          />
        </Col>
      </Form.Group>}

    <Form.Group as={Row} className="mb-2" controlId="transaction-editor-amount">
      <Form.Label column sm={3}>{t("transaction_editor_field_amount")}</Form.Label>
      <Col sm={9}>
        <InputGroup hasValidation>
          <Form.Control
            type="text"
            value={editedTransaction.amount}
            onChange={updateEditedTransaction("amount")}
            isInvalid={editedTransaction.errors.amount}
          />
          <Form.Control.Feedback type="invalid">{t(editedTransaction.errors.amount)}</Form.Control.Feedback>
        </InputGroup>
      </Col>
    </Form.Group>

    <Form.Group as={Row} className="mb-2" controlId="transaction-editor-value-date">
      <Form.Label column sm={3}>{t("transaction_editor_field_date")}</Form.Label>
      <Col sm={9}>
        <DatePicker value={editedTransaction.valueDate} onChange={updateEditedTransaction("valueDate")}/>
      </Col>
    </Form.Group>

    {editedTransaction.type !== TYPE_TRANSFER &&
      <Form.Group as={Row} className="mb-2" controlId="transaction-editor-category">
        <Form.Label column sm={3}>{t("transaction_editor_field_category")}</Form.Label>
        <Col sm={9}>
          {editedTransaction.type === TYPE_TRANSFER && t("transaction_editor_category_not_needed")}
          {editedTransaction.type !== TYPE_TRANSFER && <CategoriesDropdown
            categories={availableCategories}
            value={editedTransaction.category}
            onChange={updateEditedTransaction("category")}
          />}
        </Col>
      </Form.Group>}

    <Form.Group as={Row} className="mb-2" controlId="transaction-editor-note">
      <Form.Label column sm={3}>{t("transaction_editor_field_note")}</Form.Label>
      <Col sm={9}>
        <Form.Control
          type="text"
          value={editedTransaction.note || ""}
          onChange={updateEditedTransaction("note")}
          maxLength="200"
        />
      </Col>
    </Form.Group>

    <Form.Group as={Row} className="mb-2" controlId="transaction-editor-tags">
      <Form.Label column sm={3}>{t("transaction_editor_field_tags")}</Form.Label>
      <Col sm={9}>
        <FormControlTags value={editedTransaction.tags || ""} onChange={updateEditedTransaction("tags")}/>
      </Col>
    </Form.Group>

    <Form.Group as={Row} className="mb-2" controlId="transaction-editor-periodicity">
      <Form.Label column sm={3}>{t("transaction_editor_field_periodicity")}</Form.Label>
      <Col sm={9}>
        <AppStaticDropdown
          value={editedTransaction.periodicity}
          choices={periodicities}
          choiceDisplay={PeriodicityChoice}
          onChange={updateEditedTransaction("periodicity")}
        />
      </Col>
    </Form.Group>

    <DeleteModal
      show={deleteConfirmationModal}
      title={t("confirm_delete_title")}
      cancelLabel={t("confirm_delete_cancel_btn")}
      submitLabel={t("confirm_delete_submit_btn")}
      onCancel={hideDeleteConfirmationModal}
      onSubmit={onDeleteTransaction}
    >
      {t("confirm_delete_text")}
    </DeleteModal>
  </Editor>;
};

const PeriodicityChoice = choice => {
  return <div>{choice.label}</div>;
}

